import { IFileImport } from "../../../universal/Import/Button";
import { loadCsv, loadWorkbook } from "../../../../utils/excel/load";
import { ImportedField, parseDelimitedFile, parseWorksheet } from "../../../../utils/excel/parse";
import { ImportableFoodEntry, ImportableNutrientAmount, NutrientName } from "./types";
import { NUTRIENT_IMPORT_REGISTRY } from "./nutrientImportRegistry";
import { sortByKey } from "@notemeal/utils-sort";

export const MatchFields: (keyof ImportableFoodEntry)[] = ["name" as const];

type ParsedFoodRow = Omit<ImportableFoodEntry, "nutrientAmounts"> & Partial<Record<NutrientName, number | null>>;

type RawCsvRow = Record<string, string>;

const baseNumberFields: ImportedField<RawCsvRow, ParsedFoodRow>[] = [
  { fields: ["Default Amount"], required: false, importableField: "defaultAmount" },
  { fields: ["Weight, g"], required: true, importableField: "weight" },
  { fields: ["Carbohydrate, g"], required: true, importableField: "carbohydrate" },
  { fields: ["Protein, g"], required: true, importableField: "protein" },
  { fields: ["Fat, g"], required: true, importableField: "fat" },
];

const registryNumberFields: ImportedField<RawCsvRow, ParsedFoodRow>[] = NUTRIENT_IMPORT_REGISTRY.map(entry => ({
  fields: [entry.header],
  required: false,
  importableField: entry.nutrient,
}));

const numberFields: ImportedField<RawCsvRow, ParsedFoodRow>[] = [...baseNumberFields, ...registryNumberFields];

const stringFields: ImportedField<RawCsvRow, ParsedFoodRow>[] = [
  { fields: ["Food Name"], required: true, importableField: "name" },
  { fields: ["Serving Name"], required: false, importableField: "servingName" },
];

const dateFields: ImportedField<RawCsvRow, ParsedFoodRow>[] = [];

export const fields = sortByKey([...numberFields, ...stringFields], "required", { reverse: true }).map(x => x);

const collectNutrientAmounts = (parsed: ParsedFoodRow): ImportableNutrientAmount[] => {
  const result: ImportableNutrientAmount[] = [];
  for (const { nutrient, toCanonical } of NUTRIENT_IMPORT_REGISTRY) {
    const raw = parsed[nutrient];
    if (typeof raw !== "number") continue;
    result.push({ nutrientName: nutrient, amount: toCanonical ? toCanonical(raw) : raw });
  }
  return result;
};

const toImportableFoodEntry = (parsed: ParsedFoodRow): ImportableFoodEntry => {
  const { defaultAmount, servingName, name, weight, thumbnailUrl, groceryListCategoryId, carbohydrate, protein, fat } = parsed;
  return {
    name,
    weight,
    thumbnailUrl: thumbnailUrl ?? null,
    groceryListCategoryId: groceryListCategoryId ?? null,
    carbohydrate,
    protein,
    fat,
    defaultAmount,
    servingName,
    nutrientAmounts: collectNutrientAmounts(parsed),
  };
};

export const loadFoodEntriesFromCSV = async ({ file, onError, locale }: IFileImport): Promise<ImportableFoodEntry[] | null> => {
  const rows = await loadCsv<RawCsvRow>({ file, onError });
  if (rows === null) return null;
  const parsed = parseDelimitedFile<RawCsvRow, ParsedFoodRow>({ rows, numberFields, stringFields, dateFields, onError, locale });
  return parsed.map(toImportableFoodEntry);
};

export const loadFoodEntriesFromExcel = async ({ file, onError, locale }: IFileImport): Promise<ImportableFoodEntry[] | null> => {
  const worksheet = await loadWorkbook({ file, onError });
  if (worksheet === null) return null;
  const parsed = parseWorksheet<RawCsvRow, ParsedFoodRow>({ worksheet, numberFields, dateFields, stringFields, onError, locale });
  return parsed.map(toImportableFoodEntry);
};
